'use strict';

/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.ConcurrencyTrackingService
 * @module mx.SolutionBuilderAgent
 *
 * @requires <a href="https://github.com/JustMaier/angular-signalr-hub">Hub</a>
 * @requires <a href="https://github.com/Matrix42AG/Pandora/wiki/mx.shell.Config">mx.shell.Config</a>
 * @requires <a href="https://github.com/Matrix42AG/Pandora/blob/dev/app/services/mx-user-profile-service.js">mx.shell.UserProfileService</a>
 *
 * @description
 * The service that provides the possibility to subscribe and unsubscribe to the WebSocket based server notification channel.
 */
ConcurrencyTrackingService.$inject = [
	'$q',
	'Hub',
	'mx.shell.Config',
	'mx.shell.UserProfileService',
	'mx.shell.View',
	'mx.internationalization',
	'mx.SolutionBuilderAgent.Http',
	'mx.shell.SidePanelService',
	'mx.SolutionBuilderAgent.ContentWidgetService',
];
export default function ConcurrencyTrackingService($q, Hub, config, userProfileService, MxView, i18n, http, sidePanelService, contentWidgetService) {
	var getConnectionInfo = function (callbacks) {
		return {
			//transport: 'longPolling',
			rootPath: (config.get('restHosts').solutionBuilder || config.get('restHosts').default) + '/signalR',
			useSharedConnection: false,
			listeners: {
				NotifyObjectChanged: callbacks.change || function () {},
				NotifyObjectDeleted: callbacks.delete || function () {},
				NotifySubscriptionAdded: callbacks.subscribe || function () {},
				NotifySubscriptionRemoved: callbacks.unsubscribe || function () {},
				NotifySubscriptionsExist: callbacks.exist || function () {},
			},
			methods: ['Subscribe', 'Unsubscribe'],
			logging: false,
			stateChanged:
				callbacks.stateChanged ||
				function (state) {
					if (mx.__mx$concurrencyDebugOn) {
						switch (state.newState) {
							case $.signalR.connectionState.connecting:
								mx.console.log('connecting');
								break;
							case $.signalR.connectionState.connected:
								mx.console.log('connected');
								break;
							case $.signalR.connectionState.reconnecting:
								mx.console.log('reconnecting');
								break;
							case $.signalR.connectionState.disconnected:
								mx.console.log('disconnected');
								break;
						}
					}
				},
		};
	};

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.ConcurrencyTrackingService#subscribe
	 *
	 * @description
	 * Subscribes to the notification channel.
	 * The callbacks object should contain callback functions:
	 * - change: the callback to be called when the target object is changed externally.
	 * - subscribe: the callback to be called when another user subscribes to the notification channel.
	 * - unsubscribe: the callback to be called when another user unsubscribes to the notification channel.
	 * - exist: the callback to be called when other users are already subscribed to the notification channel.
	 *
	 * @param {object} objectId - The object identifier used to subscribe to notification channel.
	 * @param {object} callbacks - The object containing callbacks.
	 * @returns {function} - UnSubscribes and disconnects from the notification channel.
	 */
	var subscribe = function (objectId, callbacks) {
		var trackingInfo = {};
		var trackingPromise = userProfileService.get().then(function (userProfile) {
			var connectionInfo = getConnectionInfo(callbacks);
			trackingInfo.userId = userProfile.id;
			trackingInfo.userName = userProfile.firstName ? userProfile.firstName + ' ' + userProfile.lastName : userProfile.lastName;
			trackingInfo.objectId = objectId;

			var errorHandler = function (error) {
				trackingInfo.connected = false;
				if (callbacks.errorHandler) {
					callbacks.errorHandler(error);
				} else {
					mx.console.error(error);
				}
			};

			connectionInfo.errorHandler = errorHandler;
			trackingInfo.hub = new Hub('ConcurrencyTrackingHub', connectionInfo);

			return trackingInfo.hub.promise.then(
				function () {
					return trackingInfo.hub.Subscribe(trackingInfo.objectId, trackingInfo.userId, trackingInfo.userName).then(function () {
						trackingInfo.connected = true;
					});
				},
				function (error) {
					errorHandler(error);
				}
			);
		});

		var unSubscribe = function () {
			return trackingPromise.then(function () {
				return trackingInfo.connected
					? trackingInfo.hub.Unsubscribe(trackingInfo.objectId, trackingInfo.userId, trackingInfo.userName).then(function () {
							trackingInfo.connected = false;
							trackingInfo.hub.disconnect();
							return trackingInfo.connected;
					  })
					: trackingInfo.connected;
			});
		};

		unSubscribe.trackingInfo = trackingInfo;
		return unSubscribe;
	};

	function getDataChanges(entityType, oldData, newData, fullCheck) {
		var mergeInfo = {
			oldData: oldData,
			newData: newData,
		};
		return http.post('api/WidgetDialog/getDataChanges?typeName=' + entityType + '&full=' + fullCheck, mergeInfo);
	}

	function applyDataChanges(widgetId, objectId, mergeEntities) {
		var dataUri = 'api/WidgetDialog/ApplyDataChanges/' + widgetId + '/' + objectId;
		return http.post(dataUri, mergeEntities);
	}

	function showObjectChangedNotification(userName, itemName, viewInfo) {
		userName = userName || i18n.get('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyConfirmDefaultUserName');

		var promise = viewInfo.view.mergeDisabled ? $q.when([]) : getDataChanges(viewInfo.entityName, viewInfo.startEntityValue, viewInfo.finishEntityValue, !viewInfo.view.dirty);

		return promise.then(function (mergeData) {
			if (!viewInfo.view.mergeDisabled && (!Array.isArray(mergeData) || mergeData.length === 0)) {
				return { answer: 'no-changes' };
			}
			var conflictMergeEntities = mergeData.filter(function (mergeEntity) {
				return mergeEntity.IsConflict;
			});

			var dialogConfig = getConfirmationDialogConfig(conflictMergeEntities.length > 0, itemName, userName, viewInfo.view.dirty, viewInfo.view.mergeDisabled);

			return showMegeDialogs(dialogConfig, viewInfo, conflictMergeEntities, mergeData);
		});
	}

	function showObjectDeletedNotification(userName, itemName, view) {
		userName = userName || i18n.get('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyConfirmDefaultUserName');

		var dialogConfig = {
			id: 'ActionOnConcurrencyObjectDeleted',
			title: i18n.getFormatted('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyDeletedTitle', itemName),
			message: i18n.getFormatted('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyConfirmTextDeleted', itemName, userName),
			notSaved: true,
			actions: [],
		};

		dialogConfig.actions.push({
			id: 'close',
			title: i18n.get('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyConfirmClose'),
		});

		dialogConfig.actions.push({
			id: 'cancel',
			title: i18n.get('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyConfirmDoNothing'),
		});

		return sidePanelService.showConfirmationDialog(dialogConfig, view);
	}

	function showMegeDialogs(dialogConfig, viewInfo, conflictMergeEntities, mergeData) {
		return sidePanelService
			.showConfirmationDialog(dialogConfig, viewInfo.view)
			.then(function (answer) {
				switch (answer) {
					case 'cancel':
						return { answer: 'cancel' };
					case 'reload':
						return { answer: 'reload' };
					case 'manual-merge':
						return showManualMergeDialog(conflictMergeEntities, viewInfo.view);
					case 'auto-merge':
					case 'auto-merge-and-save':
						return {
							answer: 'merge',
							mergeEntities: mergeData,
							save: answer === 'auto-merge-and-save',
						};
				}
			})
			.then(function (data) {
				if (data.answer === 'merge') {
					return viewInfo.view.dirty
						? applyDataChanges(viewInfo.widgetId, viewInfo.objectId, mergeData).then(function (object) {
								data.entity = object.Entity;
								return data;
						  })
						: contentWidgetService.getDialogData(viewInfo.widgetId, viewInfo.objectId, viewInfo.view.context._archived).then(function (object) {
								data.entity = object.entity;
								return data;
						  });
				} else {
					return data;
				}
			});
	}

	function showManualMergeDialog(conflictMergeEntities, view) {
		var def = $q.defer();

		var mergeView = new MxView({
			id: 'view-concurrency-merge',
			locals: {
				conflictMergeEntities: conflictMergeEntities,
				answerPromise: def,
			},
			controllerAs: 'vm',
			controller: 'mx.SolutionBuilderAgent.ConcurrencyMergeController',
			templateUrl: 'workspaces/SolutionBuilderAgent/components/mx-concurrency-merge/mx-concurrency-merge.html',
		});

		sidePanelService.showPopupInTab(view, mergeView);

		return def.promise.then(function (data) {
			sidePanelService.hidePopupInTab(view);

			switch (data.answer) {
				case 'cancel':
					return {
						answer: 'cancel',
					};
				case 'merge':
				case 'mergeAndSave':
					return {
						answer: 'merge',
						mergeEntities: data.mergeEntities,
						save: data.answer === 'mergeAndSave',
					};
			}
		});
	}

	function getConfirmationDialogConfig(hasConflicts, itemName, userName, isDirty, mergeDisabled) {
		var dialogConfig = {
			id: 'ActionOnConcurrencyObjectChanged',
			title: i18n.getFormatted('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyConfirmTitle', itemName),
			message: i18n.getFormatted('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyConfirmTextChanged', itemName, userName),
			notSaved: true,
			actions: [],
		};

		if (!mergeDisabled) {
			if (hasConflicts) {
				dialogConfig.actions.push({
					id: 'manual-merge',
					title: i18n.get('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyManualMerge'),
				});
			} else {
				dialogConfig.actions.push({
					id: 'auto-merge',
					title: i18n.get('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyAutoMerge'),
				});
				dialogConfig.actions.push({
					id: 'auto-merge-and-save',
					title: i18n.get('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyAutoMergeAndSave'),
				});
			}
		}

		if (isDirty || mergeDisabled) {
			dialogConfig.actions.push({
				id: 'reload',
				title: i18n.get('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyConfirmCancelAndReload'),
			});
		}

		dialogConfig.actions.push({
			id: 'cancel',
			title: i18n.get('solutionBuilderAgent.layouts.dialog.dialog-content.concurrencyConfirmDoNothing'),
		});

		return dialogConfig;
	}

	function isUpdateConflict(error) {
		if (error && error.data && error.data.ExceptionName) {
			return error.data.ExceptionName.toLowerCase() === 'spsdataengineconcurrencyexception';
		}

		return false;
	}

	function isDeleted(error) {
		if (error && error.data && error.data.ExceptionName) {
			return error.data.ExceptionName.toLowerCase() === 'recordnotfoundexception';
		}

		return false;
	}

	return {
		applyDataChanges: applyDataChanges,
		showObjectChangedNotification: showObjectChangedNotification,
		showObjectDeletedNotification: showObjectDeletedNotification,
		isUpdateConflict: isUpdateConflict,
		isDeleted: isDeleted,
		subscribe: subscribe,
	};
}
